import type { ZammadTicketStats } from "@/mocks/zammadStats";

interface StatsDashboardProps {
  stats: ZammadTicketStats;
  title: string;
}

export default function StatsDashboard({ stats, title }: StatsDashboardProps) {
  const maxCategoryCount = Math.max(...stats.ticketsByCategory.map((c) => c.count), 1);
  const maxPriorityCount = Math.max(...stats.ticketsByPriority.map((p) => p.count), 1);
  const maxMonthlyCount = Math.max(
    ...stats.monthlyTrend.flatMap((m) => [m.created, m.resolved]),
    1
  );

  const formatNumber = (n: number): string => n.toLocaleString("es-MX");

  return (
    <div className="space-y-6">
      {/* Section Header */}
      <div className="flex items-center gap-3">
        <div className="w-8 h-8 rounded-lg bg-accent-100 flex items-center justify-center flex-shrink-0">
          <i className="ri-bar-chart-grouped-line text-accent-600 text-sm"></i>
        </div>
        <div>
          <h2 className="text-lg font-heading font-bold text-foreground-950">{title}</h2>
          <p className="text-xs text-foreground-500">
            {stats.totalTickets.toLocaleString("es-MX")} tickets &bull; SLA {stats.slaCompliance}% &bull; Últimos 6 meses
          </p>
        </div>
      </div>

      {/* Top Metrics Row */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        <MetricCard
          label="Total de tickets"
          value={formatNumber(stats.totalTickets)}
          icon="ri-file-list-3-line"
          color="text-foreground-700"
          bg="bg-background-100"
        />
        <MetricCard
          label="Abiertos ahora"
          value={stats.openTickets}
          icon="ri-inbox-line"
          color="text-accent-600"
          bg="bg-accent-100"
        />
        <MetricCard
          label="En progreso"
          value={stats.inProgressTickets}
          icon="ri-loader-4-line"
          color="text-primary-600"
          bg="bg-primary-100"
        />
        <MetricCard
          label="Resueltos"
          value={stats.resolvedTickets}
          icon="ri-check-line"
          color="text-emerald-600"
          bg="bg-emerald-100"
        />
        <MetricCard
          label="Cerrados"
          value={stats.closedTickets}
          icon="ri-check-double-line"
          color="text-emerald-600"
          bg="bg-emerald-100"
        />
        <MetricCard
          label="Satisfacción"
          value={`${stats.satisfactionAvg}`}
          icon="ri-star-fill"
          color="text-amber-600"
          bg="bg-amber-50"
          suffix="/5"
        />
      </div>

      {/* SLA + Resolution Row */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <div className="rounded-xl bg-background-50 border border-background-200/70 p-5">
          <p className="text-[11px] text-foreground-500 font-medium mb-3">Cumplimiento SLA</p>
          <div className="flex items-end gap-2 mb-2">
            <span className="text-2xl font-heading font-bold text-foreground-950">{stats.slaCompliance}%</span>
          </div>
          <div className="w-full h-2 rounded-full bg-background-200 overflow-hidden">
            <div
              className="h-full rounded-full bg-emerald-500 transition-all duration-700"
              style={{ width: `${stats.slaCompliance}%` }}
            ></div>
          </div>
          <p className="text-[10px] text-foreground-400 mt-2">
            {stats.slaCompliance >= 90
              ? "Excelente desempeño"
              : stats.slaCompliance >= 75
              ? "Buen nivel"
              : "Requiere atención"}
          </p>
        </div>

        <div className="rounded-xl bg-background-50 border border-background-200/70 p-5">
          <p className="text-[11px] text-foreground-500 font-medium mb-3">Tiempo promedio de resolución</p>
          <div className="flex items-end gap-2">
            <span className="text-2xl font-heading font-bold text-foreground-950">{stats.avgResolutionHours}</span>
            <span className="text-sm text-foreground-500 mb-0.5">horas</span>
          </div>
          <div className="w-full h-2 rounded-full bg-background-200 overflow-hidden mt-3">
            <div
              className="h-full rounded-full bg-accent-500 transition-all duration-700"
              style={{ width: `${Math.min((stats.avgResolutionHours / 12) * 100, 100)}%` }}
            ></div>
          </div>
          <p className="text-[10px] text-foreground-400 mt-2">Meta: &lt; 8 horas</p>
        </div>

        <div className="rounded-xl bg-background-50 border border-background-200/70 p-5">
          <p className="text-[11px] text-foreground-500 font-medium mb-3">Primera respuesta</p>
          <div className="flex items-end gap-2">
            <span className="text-2xl font-heading font-bold text-foreground-950">{stats.avgFirstResponseMinutes}</span>
            <span className="text-sm text-foreground-500 mb-0.5">minutos</span>
          </div>
          <div className="w-full h-2 rounded-full bg-background-200 overflow-hidden mt-3">
            <div
              className="h-full rounded-full bg-primary-500 transition-all duration-700"
              style={{ width: `${Math.min((stats.avgFirstResponseMinutes / 30) * 100, 100)}%` }}
            ></div>
          </div>
          <p className="text-[10px] text-foreground-400 mt-2">Meta: &lt; 30 minutos</p>
        </div>

        <div className="rounded-xl bg-background-50 border border-background-200/70 p-5">
          <p className="text-[11px] text-foreground-500 font-medium mb-3">Tickets del mes actual</p>
          <div className="flex items-end gap-2">
            <span className="text-2xl font-heading font-bold text-foreground-950">
              {stats.monthlyTrend[stats.monthlyTrend.length - 1]?.created || 0}
            </span>
          </div>
          <p className="text-[10px] text-foreground-400 mt-3">
            {stats.monthlyTrend[stats.monthlyTrend.length - 1]?.resolved || 0} resueltos este mes
          </p>
        </div>
      </div>

      {/* Category + Priority + Monthly Trend */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Tickets by Category */}
        <div className="rounded-xl bg-background-50 border border-background-200/70 p-5">
          <h3 className="text-sm font-heading font-semibold text-foreground-900 mb-4">Tickets por categoría</h3>
          <div className="space-y-3">
            {stats.ticketsByCategory.map((cat) => (
              <div key={cat.name}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs text-foreground-700 font-medium">{cat.name}</span>
                  <span className="text-[11px] text-foreground-500">{cat.count}</span>
                </div>
                <div className="w-full h-1.5 rounded-full bg-background-200 overflow-hidden">
                  <div
                    className={`h-full rounded-full ${cat.color} transition-all duration-700`}
                    style={{ width: `${(cat.count / maxCategoryCount) * 100}%` }}
                  ></div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Tickets by Priority */}
        <div className="rounded-xl bg-background-50 border border-background-200/70 p-5">
          <h3 className="text-sm font-heading font-semibold text-foreground-900 mb-4">Tickets por prioridad</h3>
          <div className="space-y-3">
            {stats.ticketsByPriority.map((pri) => (
              <div key={pri.name}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs text-foreground-700 font-medium">{pri.name}</span>
                  <span className="text-[11px] text-foreground-500">{pri.count}</span>
                </div>
                <div className="w-full h-1.5 rounded-full bg-background-200 overflow-hidden">
                  <div
                    className={`h-full rounded-full ${pri.color} transition-all duration-700`}
                    style={{ width: `${(pri.count / maxPriorityCount) * 100}%` }}
                  ></div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Monthly Trend */}
        <div className="rounded-xl bg-background-50 border border-background-200/70 p-5">
          <h3 className="text-sm font-heading font-semibold text-foreground-900 mb-4">Tendencia mensual</h3>
          <div className="flex items-end gap-2 h-32">
            {stats.monthlyTrend.map((month) => {
              const createdHeight = (month.created / maxMonthlyCount) * 100;
              const resolvedHeight = (month.resolved / maxMonthlyCount) * 100;
              return (
                <div key={month.month} className="flex-1 flex flex-col items-center gap-1.5">
                  <div className="w-full flex flex-col items-center gap-0.5" style={{ height: "100px" }}>
                    <div className="w-full flex items-end justify-center gap-0.5" style={{ height: "100%" }}>
                      <div
                        className="w-[42%] rounded-t-sm bg-accent-400/70 transition-all duration-700"
                        style={{ height: `${createdHeight}%` }}
                        title={`Creados: ${month.created}`}
                      ></div>
                      <div
                        className="w-[42%] rounded-t-sm bg-emerald-400/70 transition-all duration-700"
                        style={{ height: `${resolvedHeight}%` }}
                        title={`Resueltos: ${month.resolved}`}
                      ></div>
                    </div>
                  </div>
                  <span className="text-[10px] text-foreground-500 font-medium">{month.month}</span>
                </div>
              );
            })}
          </div>
          <div className="flex items-center justify-center gap-4 mt-3 pt-3 border-t border-background-200/60">
            <div className="flex items-center gap-1.5">
              <div className="w-2.5 h-2.5 rounded-sm bg-accent-400/70"></div>
              <span className="text-[10px] text-foreground-500">Creados</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-2.5 h-2.5 rounded-sm bg-emerald-400/70"></div>
              <span className="text-[10px] text-foreground-500">Resueltos</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function MetricCard({
  label,
  value,
  icon,
  color,
  bg,
  suffix,
}: {
  label: string;
  value: string | number;
  icon: string;
  color: string;
  bg: string;
  suffix?: string;
}) {
  return (
    <div className="rounded-xl bg-background-50 border border-background-200/70 p-4 text-center">
      <div className={`w-9 h-9 rounded-lg ${bg} flex items-center justify-center mx-auto mb-2`}>
        <i className={`${icon} ${color} text-base`}></i>
      </div>
      <div className="text-xl font-heading font-bold text-foreground-950 mb-0.5">
        {value}
        {suffix && <span className="text-xs text-foreground-400 font-normal">{suffix}</span>}
      </div>
      <p className="text-[10px] text-foreground-500 font-medium">{label}</p>
    </div>
  );
}
